import { useUserContext } from '../../contexts/UserContext';
import { hasPermission, Permission } from '../../utils/permissions';
import type { SafetyTrainingView } from '../../types/trainingTypes';
import '../../css/ActionLauncherModal.css';

interface ActionLauncherModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (view: SafetyTrainingView) => void;
    hasAssignedLibraryPlans?: boolean;
    planId?: string;
    planName?: string;
}

interface ActionCard {
    title: string;
    description: string;
    icon: string;
    iconBg: string;
    iconColor: string;
    view: SafetyTrainingView;
    badge?: string;
    badgeBg?: string;
    accentColor?: string;
    fullWidth?: boolean;
}

/**
 * Action Launcher Modal — presents plan-specific workflow entry points.
 * Triggered by "+ Plan Actions" button on the Admin Dashboard.
 *
 * Tiles: Topic List (pre-filtered by plan), Manage Schedule, User Enrollment,
 * Affidavit of Completion, and conditionally Save Plan to TotalEquipIQ Library (site admin).
 */
export const ActionLauncherModal = ({ isOpen, onClose, onSelect, planId, planName }: ActionLauncherModalProps) => {
    const { companyOwner, companyUser, businessAdmin, company } = useUserContext();
    const canManageResources = businessAdmin
        ? true  // Business admins always have access
        : hasPermission(companyOwner, companyUser, Permission.MANAGE_RESOURCE_LIBRARY, businessAdmin)
        // `canContributeToLibrary` is a legacy field absent from the Gen2 Company type;
        // preserve the existing runtime check without altering the shared type.
        && (company as { canContributeToLibrary?: boolean | null } | null)?.canContributeToLibrary === true;

    if (!isOpen) return null;

    const cards: ActionCard[] = [
        {
            title: 'Topic List',
            description: 'View and manage training topics assigned to this plan',
            icon: '📋',
            iconBg: '#eff6ff',
            iconColor: '#3b82f6',
            view: { type: 'topics', filterPlanId: planId },
        },
        {
            title: 'Manage Schedule',
            description: 'Assign topics to dates across the plan duration',
            icon: '📅',
            iconBg: '#faf5ff',
            iconColor: '#8b5cf6',
            view: { type: 'schedule-builder', planId },
        },
        {
            title: 'User Enrollment',
            description: 'Enroll users in a training plan',
            icon: '👥',
            iconBg: '#fdf2f8',
            iconColor: '#db2777',
            view: { type: 'enrollment', planId },
        },
        {
            title: 'Affidavit of Completion',
            description: 'Sign off and reset the annual compliance clock',
            icon: '✅',
            iconBg: '#ecfdf5',
            iconColor: '#059669',
            view: { type: 'affidavit' },
        },
    ];

    if (canManageResources) {
        cards.push({
            title: 'Save Plan to TotalEquipIQ Library',
            description: 'Save a training plan as a reusable template in the TotalEquipIQ Resource Library',
            icon: '📤',
            iconBg: '#fff4ed',
            iconColor: '#ea580c',
            view: { type: 'save-to-library' },
            badge: 'ADMIN',
            badgeBg: '#ea580c',
            accentColor: '#ea580c',
            fullWidth: true,
        });
    }

    const handleCardClick = (card: ActionCard) => {
        onSelect(card.view);
        onClose();
    };

    return (
        <div className="alm-overlay" onClick={onClose}>
            <div className="alm-modal" onClick={e => e.stopPropagation()}>
                <div className="alm-header">
                    <h3>{planName || 'Plan'} — Actions</h3>
                    <button className="alm-close-btn" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="alm-body">
                    <div className="alm-grid">
                        {cards.map(card => (
                            <div
                                key={card.title}
                                className={`alm-card ${card.fullWidth ? 'alm-card-full' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleCardClick(card)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleCardClick(card);
                                    }
                                }}
                                style={card.accentColor ? { borderLeft: `3px solid ${card.accentColor}` } : undefined}
                            >
                                <div
                                    className="alm-card-icon"
                                    style={{ background: card.iconBg }}
                                >
                                    {card.icon}
                                </div>
                                <div className="alm-card-content">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <p className="alm-card-title">{card.title}</p>
                                        {card.badge && (
                                            <span
                                                style={{
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    color: '#fff',
                                                    background: card.badgeBg || '#ea580c',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    whiteSpace: 'nowrap',
                                                    letterSpacing: '0.03em',
                                                }}
                                            >
                                                {card.badge}
                                            </span>
                                        )}
                                    </div>
                                    <p className="alm-card-desc">{card.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="alm-footer">
                    <button className="alm-footer-close-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
